import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { sculptureEditionsColumnsVisibility } from "@/config/dashboard/sculpture-editions";
import { publicAuctionColumnsVisibility } from "@/config/dashboard/public-auction";
import { FormLayoutElement } from "@/config/forms/general";
import { pickSculptureEditionTechniqueLine } from "@/lib/computeFields";
import i18n, { SupportedLocale } from "@/i18n";
import { Auction, SculptureEdition, SculptureEditionInAuction } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  AuctionLinkGridCard,
  SculptureEditionLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import SculptureEditionInAuctionDrawer from "./drawer/SculptureEditionInAuctionDrawer";

interface ComplexLinkProps {
  id: string;
  sculptureEdition?: SculptureEdition;
  auction?: Auction;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

/**
 * SculptureEditionInAuctionLinks component
 * @returns {JSX.Element}
 */
const SculptureEditionInAuctionLinks = ({
  id,
  sculptureEdition,
  auction,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "sculptureEditionInAuction",
    type,
  );

  const { mutate: deleteSculptureEditionInAuction } = useDeleteComplexLink(
    id,
    "sculptureEditionInAuction",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    async (id: string) => {
      await deleteSculptureEditionInAuction({
        id,
        relation: "sculptureEditionInAuction",
      });
    },
    [deleteSculptureEditionInAuction],
  );

  const [searchContent, setSearchContent] = useState("");

  const renderSculptureEditionInAuctionRowActions = useCallback(
    (data: SculptureEditionInAuction) => {
      const preview =
            type === "auction" && data.sculptureEdition
              ? {
                  previewType: "sculptureEdition" as const,
                  previewId: data.sculptureEdition.id,
                  previewElements: [
                    {
                      label: t("sculptureEditionNumeroEnregistrement"),
                      value: data.sculptureEdition.sculptureEditionNumeroEnregistrement,
                      type: "text" as const,
                    },
                    {
                      label: t("sculptureEditionNotes"),
                      value: data.sculptureEdition.sculptureEditionNotes ?? "",
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.sculptureEdition.attachments ?? [],
                      type: "image" as const,
                    },
                  ],
                }
              : type === "sculptureEdition" && data.auction
                ? {
                    previewType: "auction" as const,
                    previewId: data.auction.id,
                    previewElements: [
                      {
                        label: t("ventesDescriptionNumeroVente"),
                        value: data.auction.ventesDescriptionNumeroVente,
                        type: "text" as const,
                      },
                      {
                        label: t("ventesDescriptionResume"),
                        value: data.auction.ventesDescriptionResume ?? "",
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.auction.attachments ?? [],
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <SculptureEditionInAuctionDrawer
                  key={data.id}
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<SculptureEditionInAuction, any>[] = useMemo(() => {
    return [
      ...(type === "auction"
        ? [
            {
              accessorKey: "sculptureEditionNumeroEnregistrement",
              header: t("sculptureEditionNumeroEnregistrement"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell
                  value={
                    row.original.sculptureEdition
                      ?.sculptureEditionNumeroEnregistrement ?? ""
                  }
                />
              ),
            },
            {
              accessorKey: "sculptureEditionInAuctionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <ImageCell
                  value=""
                  attachments={row.original.sculptureEdition?.attachments ?? []}
                  domain="sculptureEdition"
                />
              ),
            },
            {
              accessorKey: "sculptureEditionTechniquePreview",
              header: t("sculptureEditionTechniquePreview"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell
                  value={
                    row.original.sculptureEdition
                      ? pickSculptureEditionTechniqueLine(
                          row.original.sculptureEdition,
                          { language: i18n.language as SupportedLocale },
                        )
                      : ""
                  }
                />
              ),
            },
            {
              accessorKey: "sculptureEditionDateRealisation",
              header: t("sculptureEditionDateRealisation"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell
                  value={
                    row.original.sculptureEdition?.sculptureEditionDateRealisation ??
                    ""
                  }
                />
              ),
            },
            {
              accessorKey: "sculptureEditionNotes",
              header: t("sculptureEditionNotes"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <HtmlCell
                  value={
                    row.original.sculptureEdition?.sculptureEditionNotes ?? ""
                  }
                />
              ),
            },
            {
              accessorKey: "ventesDescriptionNumeroVente",
              header: t("ventesDescriptionNumeroVente"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell
                  value={row.original.auction?.ventesDescriptionNumeroVente}
                />
              ),
            },
          ]
        : [
            {
              accessorKey: "ventesDescriptionNumeroVente",
              header: t("ventesDescriptionNumeroVente"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell
                  value={row.original.auction?.ventesDescriptionNumeroVente}
                />
              ),
            },
            {
              accessorKey: "vignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <ImageCell
                  value=""
                  attachments={row.original.auction?.attachments ?? []}
                  domain="auction"
                />
              ),
            },
            {
              accessorKey: "ventesDescriptionCommissaires",
              header: t("ventesDescriptionCommissaires"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell
                  value={row.original.auction?.ventesDescriptionCommissaires}
                />
              ),
            },
            {
              accessorKey: "ventesDescriptionDate",
              header: t("ventesDescriptionDate"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell value={row.original.auction?.ventesDescriptionDate} />
              ),
            },
            {
              accessorKey: "ventesDescriptionVille",
              header: t("ventesDescriptionVille"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell value={row.original.auction?.ventesDescriptionVille} />
              ),
            },
            {
              accessorKey: "ventesDescriptionPays",
              header: t("ventesDescriptionPays"),
              cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
                <TextCell value={row.original.auction?.ventesDescriptionPays} />
              ),
            },
          ]),
      {
        accessorKey: "sculptureEditionDansVentesNumeroLot",
        header: t("sculptureEditionDansVentesNumeroLot"),
        cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
          <TextCell
            value={row.original.sculptureEditionDansVentesNumeroLot ?? ""}
          />
        ),
      },
      {
        accessorKey: "sculptureEditionDansVentesReproduitPage",
        header: t("sculptureEditionDansVentesReproduitPage"),
        cell: ({ row }: { row: Row<SculptureEditionInAuction> }) => (
          <TextCell
            value={row.original.sculptureEditionDansVentesReproduitPage ?? ""}
          />
        ),
      },
      createActionsColumnDef<SculptureEditionInAuction>(({ row }) =>
        renderSculptureEditionInAuctionRowActions(row.original),
      ),
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  return (
    <div className="w-full">
      {type === "auction" ? (
        <AuctionToSculptureEditionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          auction={auction}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderSculptureEditionInAuctionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedSculptureEditions")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      ) : (
        <SculptureEditionToAuctionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          sculptureEdition={sculptureEdition}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderSculptureEditionInAuctionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedAuctions")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      )}
    </div>
  );
};

type AuctionToSculptureEditionLinksProps =
  LinksTableProps<SculptureEditionInAuction> & {
    auction: Auction | undefined;
  };

type SculptureEditionToAuctionLinksProps =
  LinksTableProps<SculptureEditionInAuction> & {
    sculptureEdition: SculptureEdition | undefined;
  };

/** Vue depuis une vente publique : liste des éditions de sculpture liées à cette vente */
const AuctionToSculptureEditionLinks = ({
  id,
  relatedData,
  fields,
  auction,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: AuctionToSculptureEditionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "auction-links-sculpture-edition-add";
  const STORAGE_KEY_LINKED = "auction-links-sculpture-edition-linked";
  const defaultVisibilityAdd = {
    ...sculptureEditionsColumnsVisibility,
    actions: true,
  };
  const defaultVisibilityLinked = {
    ...sculptureEditionsColumnsVisibility,
    ...publicAuctionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityAdd, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityAdd };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityLinked, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityLinked };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedEditions } = useGetNotRelatedResource<SculptureEdition>({
    resultType: "sculptureEdition",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsEditions: ColumnDef<SculptureEdition, any>[] = useMemo(
    () => [
      {
        accessorKey: "sculptureEditionNumeroEnregistrement",
        header: t("sculptureEditionNumeroEnregistrement"),
        cell: ({ row }: { row: Row<SculptureEdition> }) => (
          <TextCell
            value={row.original.sculptureEditionNumeroEnregistrement ?? ""}
          />
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<SculptureEdition> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="sculptureEdition"
          />
        ),
      },
      {
        accessorKey: "sculptureEditionTechniquePreview",
        header: t("sculptureEditionTechniquePreview"),
        cell: ({ row }: { row: Row<SculptureEdition> }) => (
          <TextCell
            value={pickSculptureEditionTechniqueLine(row.original, {
              language: i18n.language as SupportedLocale,
            })}
          />
        ),
      },
      {
        accessorKey: "sculptureEditionDateRealisation",
        header: t("sculptureEditionDateRealisation"),
        cell: ({ row }: { row: Row<SculptureEdition> }) => (
          <TextCell
            value={row.original.sculptureEditionDateRealisation ?? ""}
          />
        ),
      },
      createActionsColumnDef<SculptureEdition>(({ row }: { row: Row<SculptureEdition> }) => {
          const data = row.original as SculptureEdition;
          if (!auction) return null;
          return (
            <LinkDrawerRowActions
              previewType="sculptureEdition"
              previewId={data.id}
              previewElements={[
                {
                  label: t("sculptureEditionNumeroEnregistrement"),
                  value: data.sculptureEditionNumeroEnregistrement ?? "",
                  type: "text",
                },
                {
                  label: t("sculptureEditionNotes"),
                  value: data.sculptureEditionNotes ?? "",
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <SculptureEditionInAuctionDrawer
                  key={data.id}
                  fields={fields}
                  queryId={id}
                  type="auction"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    auction,
                    sculptureEdition: data,
                    sculptureEditionDansVentesNumeroLot: "",
                    sculptureEditionDansVentesReproduitPage: "",
                    sculptureEditionDansVentesEstimationBasse: "",
                    sculptureEditionDansVentesEstimationHaute: "",
                    sculptureEditionDansVentesVendu: false,
                    sculptureEditionDansVentesResultatNonListe: false,
                    sculptureEditionDansVentesNonVendu: false,
                    sculptureEditionDansVentesNonVenu: false,
                    sculptureEditionDansVentesNonReproduit: false,
                    sculptureEditionDansVentesRetire: false,
                    sculptureEditionDansVentesMiseAPrix: "",
                    sculptureEditionDansVentesPrixMarteau: "",
                    sculptureEditionDansVentesPrixAvecFrais: "",
                    sculptureEditionDansVentesNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ],
    [auction, fields, id, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsEditions}
      data={notRelatedEditions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notRelatedEditions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <SculptureEditionLinkGridCard
                  item={row.sculptureEdition}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <SculptureEditionInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="auction"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

//Composant pour Oeuvres
const SculptureEditionToAuctionLinks = ({
  id,
  relatedData,
  fields,
  sculptureEdition,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: SculptureEditionToAuctionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "sculpture-edition-links-auction-add";
  const STORAGE_KEY_LINKED = "sculpture-edition-links-auction-linked";
  const defaultVisibility = {
    ...publicAuctionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsAuctions: ColumnDef<Auction, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionNumeroVente} />;
        },
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: (props) => {
          return <HtmlCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionDate} />;
        },
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionVille} />;
        },
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => {
          return <TextCell value={row.original.ventesDescriptionPays} />;
        },
      },
      createActionsColumnDef<Auction>(({ row }) => {
          const data = row.original as Auction;
          if (!sculptureEdition) return null;
          return (
            <LinkDrawerRowActions
              previewType="auction"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("ventesDescriptionNumeroVente"),
                  value: row.original.ventesDescriptionNumeroVente,
                  type: "text",
                },
                {
                  label: t("ventesDescriptionResume"),
                  value: row.original.ventesDescriptionResume,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <SculptureEditionInAuctionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="sculptureEdition"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    auction: data,
                    sculptureEdition: sculptureEdition!,
                    sculptureEditionDansVentesNumeroLot: "",
                    sculptureEditionDansVentesReproduitPage: "",
                    sculptureEditionDansVentesEstimationBasse: "",
                    sculptureEditionDansVentesEstimationHaute: "",
                    sculptureEditionDansVentesVendu: false,
                    sculptureEditionDansVentesResultatNonListe: false,
                    sculptureEditionDansVentesNonVendu: false,
                    sculptureEditionDansVentesNonVenu: false,
                    sculptureEditionDansVentesNonReproduit: false,
                    sculptureEditionDansVentesRetire: false,
                    sculptureEditionDansVentesMiseAPrix: "",
                    sculptureEditionDansVentesPrixMarteau: "",
                    sculptureEditionDansVentesPrixAvecFrais: "",
                    sculptureEditionDansVentesNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [fields, id, sculptureEdition, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsAuctions}
      data={notAlreadyRelatedAuctions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <AuctionLinkGridCard
                  item={row.auction}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <SculptureEditionInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="sculptureEdition"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default SculptureEditionInAuctionLinks;
